import { useContext, type ReactNode, type Ref } from "react";
import classNames from "classnames";
// eslint-disable-next-line no-restricted-imports -- the design-system wrapper around Base UI
import { Avatar as AvatarBase } from "@base-ui/react/avatar";
import type { AvatarSize } from "@design-system/Avatar/Avatar";
import { AvatarGroupSizeContext } from "@design-system/Avatar/avatarGroupContext";
import { BankIcon } from "@icons";
import { styleWithVars } from "@lib/cssVars";
import {
    FIGMA_VARIANT_SIZE,
    toGlyphPercent,
    type InstitutionArtwork,
    type InstitutionGlyphBox,
} from "./institutionArtwork";
import {
    institutionCatalogue,
    type InstitutionKey,
} from "./institutionCatalogue";
import styles from "./InstitutionIcon.module.scss";

// Visual source of truth: docs/specs/2026-09-30-institution-icon.md.

export type { InstitutionKey } from "./institutionCatalogue";

/** xs 16px, sm 20px, md 24px, lg 32px, xl 40px. */
export type InstitutionIconSize = AvatarSize | "xl";

/** A catalogue brand, or a logo URL (Plaid, an integration) that may be missing or broken. */
export type InstitutionIconSource =
    | { institution: InstitutionKey; src?: never }
    | { institution?: never; src?: string | null };

export type InstitutionIconProps = InstitutionIconSource & {
    /** Defaults to the enclosing AvatarGroup's size, else "sm". */
    size?: InstitutionIconSize;
    /** Exposes the icon as a labelled image. Omit when a visible name sits next to it. */
    label?: string;
    /** Replaces the bank glyph shown when there is no logo or it fails to load. */
    fallback?: ReactNode;
    className?: string;
    ref?: Ref<HTMLSpanElement>;
};

const FALLBACK_DELAY_WITH_IMAGE_MS = 600;

const FULL_BLEED_BOX: InstitutionGlyphBox = {
    x: 0,
    y: 0,
    width: FIGMA_VARIANT_SIZE,
    height: FIGMA_VARIANT_SIZE,
};

const glyphBoxVars = ({ x, y, width, height }: InstitutionGlyphBox) => ({
    "--institution-icon-glyph-left": toGlyphPercent(x),
    "--institution-icon-glyph-top": toGlyphPercent(y),
    "--institution-icon-glyph-width": toGlyphPercent(width),
    "--institution-icon-glyph-height": toGlyphPercent(height),
});

const CatalogueGlyph = ({ artwork }: { artwork: InstitutionArtwork }) => {
    const boxVars = glyphBoxVars(artwork.box ?? FULL_BLEED_BOX);

    if ("src" in artwork.glyph) {
        return (
            <img
                src={artwork.glyph.src}
                alt=""
                className={styles.glyph}
                style={styleWithVars(boxVars)}
            />
        );
    }

    const { Glyph } = artwork.glyph;
    // .icon-color-revert keeps control recipes (Button, Tag) from repainting
    // the mark; it reverts `color` to this attribute, which inherits the
    // root's brand fg.
    return (
        <Glyph
            aria-hidden
            color="currentColor"
            className={classNames(styles.glyph, "icon-color-revert")}
            style={styleWithVars(boxVars)}
        />
    );
};

const brandVars = ({ background, glyph }: InstitutionArtwork) => ({
    "--institution-icon-brand-bg": background ?? "transparent",
    "--institution-icon-brand-fg": "color" in glyph ? glyph.color : undefined,
});

/**
 * A bank, provider or AI logo in a circle: a catalogue brand drawn from the
 * Figma InstitutionIcon, or a logo URL. With no logo, or one that fails to
 * load, it shows a bank glyph (or `fallback`). Presentational only; product
 * adapters map accounts and connections onto these props.
 * For a financial account or its connection, render `AccountInstitutionIcon`
 * (`components/institution`) instead, so the logo resolves like everywhere else.
 *
 * Stacks use `AvatarGroup` with `renderAvatar` returning `InstitutionIcon`.
 */
export const InstitutionIcon = ({
    institution,
    src,
    size,
    label,
    fallback,
    className,
    ref,
}: Readonly<InstitutionIconProps>) => {
    const groupSize = useContext(AvatarGroupSizeContext);
    const artwork: InstitutionArtwork | undefined =
        institution === undefined
            ? undefined
            : institutionCatalogue[institution];
    const hasImage = src !== undefined && src !== null && src !== "";
    const hasLabel = label !== undefined && label !== "";
    const accessibilityProps = hasLabel
        ? { role: "img", "aria-label": label }
        : { "aria-hidden": true };

    return (
        <AvatarBase.Root
            ref={ref}
            className={classNames(styles.institutionIcon, className)}
            data-shape="circle"
            data-size={size ?? groupSize ?? "sm"}
            data-source={artwork === undefined ? "image" : "catalogue"}
            style={
                artwork === undefined
                    ? undefined
                    : styleWithVars(brandVars(artwork))
            }
            {...accessibilityProps}
        >
            {artwork === undefined ? (
                <>
                    {hasImage && (
                        <AvatarBase.Image
                            src={src}
                            alt=""
                            className={styles.image}
                        />
                    )}
                    <AvatarBase.Fallback
                        delay={hasImage ? FALLBACK_DELAY_WITH_IMAGE_MS : 0}
                        className={classNames(
                            styles.fallback,
                            "icon-color-revert",
                        )}
                    >
                        {fallback ?? <BankIcon />}
                    </AvatarBase.Fallback>
                </>
            ) : (
                <CatalogueGlyph artwork={artwork} />
            )}
        </AvatarBase.Root>
    );
};
